import MainLayout from "@/components/layout/admin/MainLayout";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import Select2Multiple from "v-select2-multiple-component";

export default {
  components: {
    MainLayout,
    Loading,
    Select2Multiple
  },
  data() {
    return {
      isFilterOpen: false,
      isLoading: false,
      searchData: {
        acd_year_id: null
      },
      academicYearOptions: [],
      reportData: {
        years: []
      }
    };
  },
  computed: {
    /**
     * Pre-computed yearly summary tables.
     * Transposes the API response (payment methods as rows) into the
     * design (summary type as rows, payment method names as columns).
     */
    yearlySummaries() {
      const years = (this.reportData && this.reportData.years) || [];
      return years.map((y) => this.buildYearSummary(y));
    },
    /** True when at least one year of data is available to render. */
    hasReportData() {
      return this.yearlySummaries.length > 0;
    }
  },
  methods: {
    /**
     * Cast a value to a finite number, defaulting to 0.
     * @param {*} value
     * @returns {number}
     */
    toNumber(value) {
      const n = parseFloat(value);
      return isNaN(n) ? 0 : n;
    },
    /**
     * Format a number for display. Returns "—" for zero/falsy values.
     * @param {number} value
     * @returns {string}
     */
    formatNumber(value) {
      const n = this.toNumber(value);
      if (!n) return "—";
      return n.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    /**
     * Format a signed number. Used for Total OUT.
     * @param {number} value
     * @returns {string}
     */
    formatSignedNumber(value) {
      const n = this.toNumber(value);
      if (!n) return "—";
      const sign = n < 0 ? "-" : "";
      const absStr = Math.abs(n).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      return sign + absStr;
    },
    /**
     * Build a per-year summary with transposed data.
     * @param {object} yearData - Raw year data from API
     * @returns {object}
     */
    buildYearSummary(yearData) {
      const raw = yearData.payment_methods || [];
      const paymentMethods = raw.map((pm) => ({
        id: pm.payment_method_id,
        name: pm.payment_method
      }));
      const beginning = raw.map((pm) => this.toNumber(pm.beginning_balance));
      const totalIn = raw.map((pm) => this.toNumber(pm.total_in));
      const totalOut = raw.map((pm) => -Math.abs(this.toNumber(pm.total_out)));
      const ending = raw.map((pm) => this.toNumber(pm.ending_balance));
      const periodName = yearData.period_name || "";
      return {
        acdYearId: yearData.acdYearId,
        year: yearData.year ? String(yearData.year) : "",
        periodName,
        yearLabel: periodName || "Academic Year",
        startDate: yearData.from_date,
        endDate: yearData.to_date,
        paymentMethods,
        beginning,
        totalIn,
        totalOut,
        ending
      };
    },
    /** Fetch academic years for dropdown from API. */
    async getAcademicYears() {
      await axios.get("/cashflow/academic-years")
        .then((response) => {
          this.academicYearOptions = response.data;
          this.searchData.acd_year_id = this.resolveDefaultAcademicYearId(this.academicYearOptions);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    /** Resolve the default academic year using today and returned start/end dates. */
    resolveDefaultAcademicYearId(years) {
      const today = new Date().toISOString().slice(0, 10);
      if (!Array.isArray(years) || years.length === 0) {
        return null;
      }

      const currentYear = years.find((year) => {
        return year.start_date && year.end_date && year.start_date <= today && today <= year.end_date;
      });
      if (currentYear) {
        return currentYear.id;
      }

      const previousYears = years
        .filter((year) => year.end_date && year.end_date < today)
        .sort((a, b) => (a.end_date < b.end_date ? 1 : a.end_date > b.end_date ? -1 : 0));

      if (previousYears.length > 0) {
        return previousYears[0].id
      }

      return years.length > 0 ? years[0].id : null;
    },
    /** Auto select default academic year if backend provided one. */
    autoSelectAcademicYear() {
      const defaultYear = this.academicYearOptions.find((year) => year.default);
      if (defaultYear) {
        this.searchData.acd_year_id = defaultYear.id;
      }
    },
    /** Reset search to default academic year. */
    reset() {
      this.searchData.acd_year_id = null;
      this.autoSelectAcademicYear();
      this.search();
    },
    /** Fetch yearly cashflow report for selected academic year. */
    search() {
      if (!this.searchData.acd_year_id) {
        return;
      }
      this.isLoading = true;
      this.reportData = { years: [] };
      axios
        .get("/cashflow/yearly", {
          params: {
            acd_year_id: this.searchData.acd_year_id
          }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data) {
            this.reportData = response.data;
          }
        })
        .catch((error) => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
          console.error(error);
        });
    }
  },
  async mounted() {
    await this.getAcademicYears();
    this.autoSelectAcademicYear();
    this.search();
  }
};
